"use client";

import type { ParcelPreview } from "@/lib/parcelq/types";
import {
  Bounds_From_Ring,
  Format_Latitude,
  Format_Longitude,
  LonLat_To_Svg_Point,
  Parcel_Wgs84_Centroid,
  Ring_To_Svg_Points,
  Ring_To_Wgs84,
  Tiles_For_Viewport,
  Zoom_For_Bounds,
} from "@/lib/parcelq/map-preview";
import { ParcelQ_Label_Mono_Class } from "./parcelq-styles";

const Map_Width = 420;
const Map_Height = 360;

type Parcel_Map_Preview_Props = {
  Parcel: ParcelPreview;
};

export function Parcel_Map_Preview({ Parcel }: Parcel_Map_Preview_Props) {
  const Ring_Wgs84 = Ring_To_Wgs84(Parcel.rings);
  const Centroid = Parcel_Wgs84_Centroid(Parcel.latitude, Parcel.longitude, Ring_Wgs84);
  const Bounds = Bounds_From_Ring(Ring_Wgs84, Centroid);
  const Zoom = Zoom_For_Bounds(Bounds, Map_Width, Map_Height);
  const Tiles = Tiles_For_Viewport(Centroid, Zoom, Map_Width, Map_Height);
  const Polygon_Points = Ring_To_Svg_Points(Ring_Wgs84, Centroid, Zoom, Map_Width, Map_Height);
  const Pin = LonLat_To_Svg_Point(
    Centroid.lon,
    Centroid.lat,
    Centroid,
    Zoom,
    Map_Width,
    Map_Height,
  );

  return (
    <div
      className="relative mx-auto w-full max-w-[420px]"
      role="img"
      aria-label="Parcel map with centroid pin"
    >
      <div
        className="relative overflow-hidden rounded-lg border border-pq-border bg-slate-100 shadow-inner shadow-amber-950/10"
        style={{ width: Map_Width, height: Map_Height, maxWidth: "100%" }}
      >
        {Tiles.map((Tile) => (
          <img
            key={`${Zoom}-${Tile.x}-${Tile.y}`}
            src={`https://tile.openstreetmap.org/${Zoom}/${Tile.x}/${Tile.y}.png`}
            alt=""
            draggable={false}
            className="pointer-events-none absolute max-w-none"
            style={{ left: Tile.left, top: Tile.top, width: 256, height: 256 }}
          />
        ))}

        <svg
          viewBox={`0 0 ${Map_Width} ${Map_Height}`}
          className="pointer-events-none absolute inset-0 h-full w-full text-amber-800"
          aria-hidden
        >
          {Polygon_Points ? (
            <polygon
              points={Polygon_Points}
              fill="rgba(34,211,238,0.18)"
              stroke="currentColor"
              strokeWidth="2"
            />
          ) : (
            <circle
              cx={Pin.x}
              cy={Pin.y}
              r="22"
              fill="rgba(34,211,238,0.12)"
              stroke="currentColor"
              strokeWidth="1.5"
            />
          )}
          <circle cx={Pin.x} cy={Pin.y} r="6" fill="#22d3ee" stroke="#0a1424" strokeWidth="2" />
          <circle cx={Pin.x} cy={Pin.y - 14} r="3" fill="#22d3ee" />
          <line
            x1={Pin.x}
            y1={Pin.y - 11}
            x2={Pin.x}
            y2={Pin.y - 5}
            stroke="#22d3ee"
            strokeWidth="2"
          />
        </svg>
      </div>

      <div className="absolute bottom-3 left-3 rounded-lg border border-pq-border bg-pq-panel/95 px-3 py-2 backdrop-blur-sm">
        <p className={ParcelQ_Label_Mono_Class}>Centroid</p>
        <p className="font-mono text-xs text-slate-200">
          {Format_Latitude(Centroid.lat)}, {Format_Longitude(Centroid.lon)}
        </p>
      </div>

      <p className="absolute right-2 bottom-1 text-[10px] text-pq-fg-subtle">
        © OpenStreetMap
      </p>
    </div>
  );
}
